import type { CharacterInventory } from "@engine/inventory/CharacterInventory.js";
import {
  emptyInventory,
  addItem,
  remainingCapacity,
} from "@engine/inventory/CharacterInventory.js";
import { loadItem } from "@engine/loaders/ItemLoader.js";
import { GOLD_ITEM_ID } from "@engine/inventory/goldItem.js";
import { PartyStore } from "./PartyStore.js";
import { EventBus } from "@engine/EventBus.js";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface InventoryStoreJSON {
  inventories: Record<string, CharacterInventory>;
}

// ── State ─────────────────────────────────────────────────────────────────────

let inventories = new Map<string, CharacterInventory>();

// ── Store ─────────────────────────────────────────────────────────────────────

export const InventoryStore = {
  get(instanceId: string): CharacterInventory {
    return inventories.get(instanceId) ?? emptyInventory();
  },

  set(instanceId: string, inv: CharacterInventory): void {
    inventories.set(instanceId, inv);
    EventBus.emit("ui:party_update", {});
  },

  reset(): void {
    inventories = new Map();
  },

  toJSON(): InventoryStoreJSON {
    const result: Record<string, CharacterInventory> = {};
    for (const [id, inv] of inventories) {
      result[id] = inv;
    }
    return { inventories: result };
  },

  loadJSON(data: InventoryStoreJSON): void {
    inventories = new Map(Object.entries(data.inventories));
  },
};

// ── give_item wiring ──────────────────────────────────────────────────────────
//
// Routes items to whichever party member has the most remaining carry capacity.
// Mirrors the lootAll algorithm from I04.

EventBus.on("inventory:give_item", ({ itemId, qty }) => {
  // Gold never enters a character inventory — it lives in the shared
  // party purse (I19). Loot tables and Ink both grant it by this id.
  if (itemId === GOLD_ITEM_ID) {
    PartyStore.earnGold(qty);
    EventBus.emit("inventory:gold_changed", { gold: PartyStore.getGold(), delta: qty });
    return;
  }
  void (async () => {
    const item = await loadItem(itemId);
    if (item === null) return;

    const members = PartyStore.getMembers();
    if (members.length === 0) return;

    // Pick the member with the most remaining capacity.
    let bestId = members[0]!.instanceId;
    let bestCapacity = -Infinity;
    for (const m of members) {
      const inv = InventoryStore.get(m.instanceId);
      const cap = remainingCapacity(inv, m.abilities.STR);
      if (cap > bestCapacity) {
        bestCapacity = cap;
        bestId = m.instanceId;
      }
    }

    const member = members.find((m) => m.instanceId === bestId);
    if (!member) return;

    const inv = InventoryStore.get(bestId);
    const { inventory: next } = addItem(inv, item, qty, member.abilities.STR);
    InventoryStore.set(bestId, next);
  })();
});
